import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { MapPin, Clock, Loader2, LocateFixed, Truck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };

const DEFAULT_HOURS = DAYS.reduce((acc, d) => {
  acc[d] = { is_open: true, open: '09:00', close: '21:00' };
  return acc;
}, {});

export default function VendorServiceSettings({ token }) {
  const [settings, setSettings] = useState({
    location_lat: null, location_lng: null, location_address: '',
    delivery_radius_km: 10, operating_hours: DEFAULT_HOURS
  });
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/addresses/vendor/service-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setSettings(prev => ({
          ...prev,
          ...data,
          operating_hours: data.operating_hours || DEFAULT_HOURS,
          delivery_radius_km: data.delivery_radius_km || 10,
        }));
      } catch (err) { console.error('Failed to load settings:', err); }
    };
    load();
  }, [token]);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setSettings(s => ({ ...s, location_lat: latitude, location_lng: longitude }));
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
          const data = await res.json();
          if (data.display_name) {
            setSettings(s => ({ ...s, location_address: data.display_name }));
          }
        } catch (err) { console.error('Geocoding failed:', err); }
        setLocating(false);
        toast.success('Location captured');
      },
      () => { setLocating(false); toast.error('Failed to get location'); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/addresses/vendor/service-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(settings)
      });
      if (res.ok) { toast.success('Service settings saved'); }
      else { const d = await res.json(); toast.error(d.detail || 'Failed to save'); }
    } catch { toast.error('Network error'); }
    setSaving(false);
  };

  const updateHours = (day, field, value) => {
    setSettings(s => ({
      ...s,
      operating_hours: {
        ...s.operating_hours,
        [day]: { ...s.operating_hours[day], [field]: value }
      }
    }));
  };

  return (
    <div className="space-y-4" data-testid="vendor-service-settings">
      {/* Location */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><MapPin className="h-4 w-4 text-teal-600" /> Store Location</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">Address</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Store address"
                value={settings.location_address || ''}
                onChange={e => setSettings(s => ({ ...s, location_address: e.target.value }))}
                data-testid="vendor-location-address"
              />
              <Button size="sm" variant="outline" onClick={getCurrentLocation} disabled={locating} data-testid="vendor-get-location-btn">
                {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          {settings.location_lat && (
            <p className="text-xs text-teal-600">
              <MapPin className="h-3 w-3 inline mr-1" />
              {settings.location_lat.toFixed(4)}, {settings.location_lng?.toFixed(4)}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Delivery Radius */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><Truck className="h-4 w-4 text-blue-600" /> Delivery Range</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <Label className="text-xs whitespace-nowrap">Max delivery distance</Label>
            <Input
              type="number"
              min={1}
              max={100}
              value={settings.delivery_radius_km || ''}
              onChange={e => setSettings(s => ({ ...s, delivery_radius_km: Number(e.target.value) }))}
              className="w-20"
              data-testid="delivery-radius-input"
            />
            <span className="text-sm text-slate-500">km</span>
          </div>
        </CardContent>
      </Card>

      {/* Operating Hours */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><Clock className="h-4 w-4 text-amber-600" /> Operating Hours</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {DAYS.map(day => {
              const h = settings.operating_hours?.[day] || { is_open: true, open: '09:00', close: '21:00' };
              return (
                <div key={day} className="flex items-center gap-3" data-testid={`hours-${day}`}>
                  <span className="text-xs font-medium w-8 text-slate-600">{DAY_LABELS[day]}</span>
                  <Switch
                    checked={h.is_open}
                    onCheckedChange={v => updateHours(day, 'is_open', v)}
                  />
                  {h.is_open ? (
                    <>
                      <Input type="time" value={h.open} onChange={e => updateHours(day, 'open', e.target.value)} className="w-28 h-8 text-xs" />
                      <span className="text-xs text-slate-400">to</span>
                      <Input type="time" value={h.close} onChange={e => updateHours(day, 'close', e.target.value)} className="w-28 h-8 text-xs" />
                    </>
                  ) : (
                    <span className="text-xs text-red-500">Closed</span>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Button onClick={save} disabled={saving} className="w-full" data-testid="save-service-settings-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
        Save Service Settings
      </Button>
    </div>
  );
}
